import { useState } from "react";
import axios from "axios";
import { AlertTriangle, FileSearch, CheckCircle2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PkiCrossDocCheck · UI-Box „Plausibilitätscheck mit SIS und Maßnahmenplan".
 *
 * Props:
 *   sisText · String
 *   planText · String
 *   berichtText · String
 *   customerLabel? · String
 *   onResult? · (data) => void  — Callback, optional
 *
 * Verhalten:
 *   - Button „Plausibilität prüfen" ruft POST /pflege-ki/cross-doc-check.
 *   - Bei roten Hinweisen wird eine Begründungs-Textarea + Freigabe-Button angezeigt.
 */
export default function PkiCrossDocCheck({ sisText, planText, berichtText, nbaText, customerLabel, onResult }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [begruendung, setBegruendung] = useState("");
  const [freigegeben, setFreigegeben] = useState(false);

  const canCheck = (sisText || "").trim().length > 0
    && (planText || "").trim().length > 0
    && (berichtText || "").trim().length > 0;

  const runCheck = async () => {
    setBusy(true);
    setError(null);
    setFreigegeben(false);
    try {
      const r = await axios.post(`${API}/pflege-ki/cross-doc-check`, {
        sis_text: sisText, plan_text: planText, bericht_text: berichtText,
        nba_text: nbaText || null,
        customer_label: customerLabel || null,
      }, { withCredentials: true, timeout: 15000 });
      setResult(r.data);
      onResult?.(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Unbekannter Fehler.");
    } finally {
      setBusy(false);
    }
  };

  const freigeben = async () => {
    if (!result?.audit_id) return;
    if (result.severity === "rot" && !begruendung.trim()) {
      toast.error("Bei roten Hinweisen ist eine Begründung pflichtig.");
      return;
    }
    try {
      await axios.post(`${API}/pflege-ki/cross-doc-check/freigabe`, {
        audit_id: result.audit_id,
        freigabe_trotz_rot: result.severity === "rot",
        begruendung,
      }, { withCredentials: true });
      setFreigegeben(true);
      toast.success("Freigabe gespeichert.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Freigabe fehlgeschlagen.");
    }
  };

  return (
    <section
      data-testid="pki-cross-doc-check"
      className="rounded-2xl border border-cream-300 bg-cream-50/40 p-5 md:p-6 space-y-4"
    >
      <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-3">
          <FileSearch className="h-5 w-5 text-clay-dark" strokeWidth={2} />
          <div>
            <h3 className="text-[14px] font-bold tracking-[0.04em] uppercase text-clay-dark">
              Plausibilitätscheck · SIS · Maßnahmenplan · Pflegebericht
            </h3>
            <p className="text-[11.5px] text-sand-600 mt-0.5">
              Prüft Konsistenz und Lücken zwischen den drei Dokumenten (deterministisch, ohne KI).
            </p>
          </div>
        </div>
        <Button
          data-testid="pki-cross-doc-check-run"
          onClick={runCheck}
          disabled={!canCheck || busy}
          className="bg-clay-dark hover:bg-clay-dark/90 text-cream"
        >
          {busy ? "Prüfe…" : "Plausibilität prüfen"}
        </Button>
      </header>

      {!canCheck && (
        <p className="text-[12px] text-sand-500 italic">
          Bitte SIS, Maßnahmenplan und Pflegebericht ausfüllen.
        </p>
      )}

      {error && (
        <p className="text-[13px] text-brand-darkred" data-testid="pki-cross-doc-error">
          {error}
        </p>
      )}

      {result && (
        <ResultBlock
          result={result}
          freigegeben={freigegeben}
          begruendung={begruendung}
          setBegruendung={setBegruendung}
          freigeben={freigeben}
        />
      )}
    </section>
  );
}

function ResultBlock({ result, freigegeben, begruendung, setBegruendung, freigeben }) {
  const sev = result.severity;
  const cls = sev === "rot"
    ? "border-brand-darkred/45 bg-brand-darkred/8 text-brand-darkred"
    : sev === "gelb"
      ? "border-saffron/55 bg-saffron/15 text-clay-dark"
      : "border-sage-dark/35 bg-sage-dark/10 text-sage-dark";
  const Icon = sev === "gruen" ? CheckCircle2 : AlertTriangle;

  // Sortierung: rot zuerst, dann gelb
  const sorted = [...result.findings].sort((a, b) => {
    const order = { rot: 0, gelb: 1, gruen: 2 };
    return (order[a.severity] || 9) - (order[b.severity] || 9);
  });

  return (
    <div className="space-y-3" data-testid="pki-cross-doc-result">
      <div className={`rounded-xl border ${cls} p-4 flex items-start gap-3`}>
        <Icon className="h-5 w-5 mt-0.5 flex-shrink-0" strokeWidth={2.2} />
        <div>
          <p className="text-[13.5px] font-bold">{result.label}</p>
          <p className="text-[12px] mt-1">
            {result.count} Hinweise · geprüft: {result.documents_checked.join(", ")}
          </p>
        </div>
      </div>

      {sorted.length > 0 && (
        <ul className="space-y-2" data-testid="pki-cross-doc-list">
          {sorted.map((f, idx) => (
            <li
              key={idx}
              data-testid={`pki-cross-doc-item-${idx}`}
              className={`rounded-lg border p-3 space-y-1.5 ${
                f.severity === "rot"
                  ? "border-brand-darkred/35 bg-white"
                  : "border-saffron/45 bg-white"
              }`}
            >
              <div className="flex flex-wrap items-baseline gap-2 text-[11.5px]">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full uppercase font-bold tracking-[0.08em] ${
                  f.severity === "rot" ? "bg-brand-darkred/15 text-brand-darkred" : "bg-saffron/30 text-clay-dark"
                }`}>
                  {f.severity}
                </span>
                <span className="font-bold text-sand-700">{f.label}</span>
                <span className="text-sand-500">·</span>
                <span className="text-sand-500 italic">{f.kategorie}</span>
              </div>
              <p className="text-[13px] text-sand-800 leading-snug">
                <span className="font-semibold">Problem:</span> {f.problem}
              </p>
              {f.warum_relevant && (
                <p className="text-[12px] text-sand-700 leading-snug">
                  <span className="font-semibold">Warum relevant:</span> {f.warum_relevant}
                </p>
              )}
              {f.verbesserungsvorschlag && (
                <p className="text-[12.5px] text-sage-dark leading-snug">
                  <span className="font-semibold">Vorschlag:</span> {f.verbesserungsvorschlag}
                </p>
              )}
              {(f.fundstelle_sis || f.fundstelle_plan || f.fundstelle_bericht) && (
                <details className="text-[11.5px] text-sand-600 mt-1">
                  <summary className="cursor-pointer hover:text-clay-dark">Fundstellen anzeigen</summary>
                  <div className="mt-1.5 space-y-1 pl-3 border-l-2 border-cream-300">
                    {f.fundstelle_sis && <p><strong>SIS:</strong> „{f.fundstelle_sis}&ldquo;</p>}
                    {f.fundstelle_plan && <p><strong>Plan:</strong> „{f.fundstelle_plan}&ldquo;</p>}
                    {f.fundstelle_bericht && <p><strong>Bericht:</strong> „{f.fundstelle_bericht}&ldquo;</p>}
                    {f.fundstelle_nba && <p><strong>NBA:</strong> „{f.fundstelle_nba}&ldquo;</p>}
                  </div>
                </details>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Freigabe-Block */}
      {sev !== "gruen" && !freigegeben && (
        <div className="rounded-xl border border-cream-300 bg-white p-4 space-y-2.5" data-testid="pki-cross-doc-freigabe-block">
          {sev === "rot" && (
            <>
              <p className="text-[12.5px] text-brand-darkred font-semibold">
                Rote Hinweise: Freigabe nur mit fachlicher Begründung möglich.
              </p>
              <textarea
                data-testid="pki-cross-doc-begruendung"
                value={begruendung}
                onChange={(e) => setBegruendung(e.target.value)}
                placeholder={'Bitte begründen, warum trotz roter Hinweise freigegeben wird (z. B. „Lagerung wurde mündlich übergeben, Eintrag wird nachgetragen").'}
                className="w-full min-h-[80px] rounded-lg border border-cream-300 px-3 py-2 text-[13px] focus:outline-none focus:border-clay-dark"
              />
            </>
          )}
          <Button
            data-testid="pki-cross-doc-freigeben"
            onClick={freigeben}
            disabled={sev === "rot" && !begruendung.trim()}
            className="bg-sage-dark hover:bg-sage-dark/90 text-cream"
          >
            <ShieldCheck className="h-4 w-4 mr-2" strokeWidth={2} />
            {sev === "rot" ? "Mit Begründung freigeben" : "Freigeben"}
          </Button>
        </div>
      )}
      {freigegeben && (
        <p className="text-[12.5px] text-sage-dark flex items-center gap-2" data-testid="pki-cross-doc-freigegeben">
          <CheckCircle2 className="h-4 w-4" /> Freigabe wurde gespeichert (Audit-ID: {result.audit_id.slice(0, 8)}…).
        </p>
      )}
    </div>
  );
}
